import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { ClubLogo } from "../../../components/ClubLogo/ClubLogo";
import { AuthenticationModal } from "../../../features/Authentication/components/AuthenticationModal";
import { useFlags } from "../../../utils/launchDarkly";
import { InvitePanel_Booking$key } from "./__generated__/InvitePanel_Booking.graphql";
import { InvitePanel_Session$key } from "./__generated__/InvitePanel_Session.graphql";
import { InviteCardActionButton } from "./components/InviteCardActionButton/InviteCardActionButton";
import { InviteCardBookingInfoSection } from "./components/InviteCardBookingInfoSection/InviteCardBookingInfoSection";
import { InviteCardTopSection } from "./components/InviteCardTopSection/InviteCardTopSection";
import { SignInFooter } from "./components/SignInFooter/SignInFooter";

export const InvitePanel = ({
  bookingRef,
  sessionRef,
  onConfirm,
  navigateHome,
}: {
  bookingRef: InvitePanel_Booking$key | null;
  sessionRef: InvitePanel_Session$key | null;
  onConfirm: () => void;
  navigateHome?: () => void;
}) => {
  const { newTopbar } = useFlags();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const data = useFragment(InvitePanelFragmentString, bookingRef);
  const session = useFragment(InvitePanelSessionFragmentString, sessionRef);
  const club = session?.currentClientProfile?.member?.club;
  const clubName = club?.name ?? t`Join Booking`;
  const clubSlug = club?.slug;
  const hasAnonymousCustomerProfile =
    session?.currentClientProfile?.__typename === "AnonymousCustomerProfile";

  const handleContinuePress = () => {
    if (hasAnonymousCustomerProfile) {
      setIsModalOpen(true);
    } else {
      onConfirm();
    }
  };

  return (
    <VStack
      flex={1}
      maxWidth={568}
      width={"100%"}
      marginLeft="auto"
      marginRight="auto"
    >
      <VStack flex={1} width="100%">
        {newTopbar ? null : (
          <PanelHeader
            header={clubName}
            paddingVariant="medium"
            outerLeftComponent={
              <Button
                accessibilityLabel={t`Close button`}
                testID="modal-close-btn"
                variant="secondary"
                onPress={navigateHome}
                leftIcon={<CloseIcon />}
              />
            }
            outerRightComponent={<ClubLogo clubRef={club ?? null} />}
          />
        )}
        <VStack
          width={360}
          marginLeft="auto"
          marginRight="auto"
          marginTop={6}
          borderWidth={1}
          borderColor="outline-default"
          style={{ borderRadius: 20 }}
          overflow="hidden"
          shadowColor="black"
          shadowOpacity={0.08}
          shadowOffset={{ width: 0, height: 16 }}
          shadowRadius={24}
        >
          <InviteCardTopSection bookingRef={data ?? null} />
          <InviteCardBookingInfoSection bookingRef={data ?? null} />
          <InviteCardActionButton
            bookingRef={data ?? null}
            onContinuePress={handleContinuePress}
          />
        </VStack>
      </VStack>
      <SignInFooter
        sessionRef={session ?? null}
        onSignInPress={() => {
          setIsModalOpen(true);
        }}
      />
      <AuthenticationModal
        isOpen={isModalOpen}
        onComplete={onConfirm}
        onClose={() => {
          setIsModalOpen(false);
        }}
        clubSlug={clubSlug}
      />
    </VStack>
  );
};

const InvitePanelFragmentString = graphql`
  fragment InvitePanel_Booking on Booking {
    ...InviteCardTopSection_Booking
    ...InviteCardBookingInfoSection_Booking
    ...InviteCardActionButton_Booking
    ...useHandleUserAlreadyInBooking_Booking
  }
`;

const InvitePanelSessionFragmentString = graphql`
  fragment InvitePanel_Session on Session {
    ...SignInFooter_session
    currentClientProfile {
      __typename
      member {
        club {
          slug
          name
          ...ClubLogo_Club
        }
      }
    }
  }
`;
